import { useEffect, useMemo, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, Search, Users } from "lucide-react";
import { z } from "zod";
import { useOrg } from "@/lib/org/OrgContext";
import { companiesQueries, isManager, type CompanyDto } from "@/lib/companies/api";
import {
  classifyEmployee,
  employeesQueries,
  type EmployeeDirectoryRowDto,
  type EmployeeStatus,
} from "@/lib/employees/api";
import { ApiError } from "@/api/fetch";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { PageContainer } from "@/components/layout/WorkspaceShell";
import { PageHeader } from "@/components/layout/PageHeader";
import { cn } from "@/lib/utils";
import { formatNumber, formatRelative } from "@/lib/format";

const searchSchema = z.object({
  companyId: z.string().optional(),
  status: z.enum(["all", "fully-synced", "not-in-javeed", "orphan-wages"]).optional(),
});

export const Route = createFileRoute("/o/$slug/employees/")({
  validateSearch: searchSchema,
  component: EmployeesDirectoryPage,
});

function EmployeesDirectoryPage() {
  const org = useOrg();
  const navigate = useNavigate({ from: Route.fullPath });
  const search = Route.useSearch();

  const companies = useQuery(companiesQueries.all());
  // Drive the company picker: prefer the URL param; fall back to first
  // available; once we have one, write it back to the URL so reloads stick.
  const activeCompanyId = search.companyId ?? companies.data?.[0]?.id;

  useEffect(() => {
    if (!search.companyId && activeCompanyId) {
      void navigate({ search: (prev) => ({ ...prev, companyId: activeCompanyId }), replace: true });
    }
  }, [search.companyId, activeCompanyId, navigate]);

  return (
    <PageContainer>
      <PageHeader
        title="Personal"
        description="Übersicht der aktiven Ordio-Identitäten je Unternehmen, mit Javeed-Status."
      />

      <CompanyTabs
        companies={companies.data ?? []}
        loading={companies.isLoading}
        error={companies.error}
        activeId={activeCompanyId}
        onChange={(id) =>
          void navigate({ search: (prev) => ({ ...prev, companyId: id }), replace: false })
        }
      />

      {activeCompanyId ? (
        <DirectoryForCompany
          slug={org.slug}
          company={companies.data?.find((c) => c.id === activeCompanyId)}
          companyId={activeCompanyId}
          statusFilter={search.status ?? "all"}
          onStatusChange={(status) =>
            void navigate({
              search: (prev) => ({ ...prev, status: status === "all" ? undefined : status }),
              replace: true,
            })
          }
        />
      ) : (
        !companies.isLoading && (
          <p className="mt-6 text-sm text-muted-foreground">
            Keine Unternehmen für diese Organisation sichtbar.
          </p>
        )
      )}
    </PageContainer>
  );
}

// ── Company tab strip ───────────────────────────────────────────────────────

function CompanyTabs({
  companies,
  loading,
  error,
  activeId,
  onChange,
}: {
  companies: CompanyDto[];
  loading: boolean;
  error: Error | null;
  activeId: string | undefined;
  onChange: (companyId: string) => void;
}) {
  if (loading) {
    return (
      <div className="mb-4 flex gap-2">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-9 w-32" />
        ))}
      </div>
    );
  }
  if (error) {
    return (
      <p className="mb-4 text-sm text-destructive">
        Konnte Unternehmen nicht laden: {(error as Error).message}
      </p>
    );
  }
  if (companies.length === 0) return null;

  return (
    <div className="mb-4 flex flex-wrap items-center gap-2 border-b">
      {companies.map((c) => {
        const active = c.id === activeId;
        return (
          <button
            key={c.id}
            type="button"
            onClick={() => onChange(c.id)}
            className={cn(
              "relative -mb-px border-b-2 px-3 py-2 text-sm transition-colors",
              active
                ? "border-primary font-medium text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground",
            )}
          >
            {c.name}
            {!isManager(c.role) && (
              <span className="ml-2 text-[10px] uppercase text-muted-foreground">Viewer</span>
            )}
          </button>
        );
      })}
    </div>
  );
}

// ── Directory for one company ───────────────────────────────────────────────

function DirectoryForCompany({
  slug,
  company,
  companyId,
  statusFilter,
  onStatusChange,
}: {
  slug: string;
  company: CompanyDto | undefined;
  companyId: string;
  statusFilter: EmployeeStatus | "all";
  onStatusChange: (status: EmployeeStatus | "all") => void;
}) {
  const [textFilter, setTextFilter] = useState("");
  const directory = useQuery(employeesQueries.directory(slug, companyId));

  const filtered = useMemo(() => {
    const text = textFilter.trim().toLowerCase();
    const rows = directory.data ?? [];
    return rows.filter((r) => {
      if (statusFilter !== "all" && classifyEmployee(r) !== statusFilter) return false;
      if (!text) return true;
      return (
        r.employeeDisplayName.toLowerCase().includes(text) ||
        (r.email ?? "").toLowerCase().includes(text) ||
        (r.phone ?? "").toLowerCase().includes(text)
      );
    });
  }, [directory.data, textFilter, statusFilter]);

  const counts = useMemo(() => {
    const rows = directory.data ?? [];
    return {
      total: rows.length,
      fullySynced: rows.filter((r) => classifyEmployee(r) === "fully-synced").length,
      notInJaveed: rows.filter((r) => classifyEmployee(r) === "not-in-javeed").length,
      orphanWages: rows.filter((r) => classifyEmployee(r) === "orphan-wages").length,
    };
  }, [directory.data]);

  return (
    <>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            value={textFilter}
            onChange={(e) => setTextFilter(e.target.value)}
            placeholder="Name, E-Mail, Telefon …"
            className="w-72 pl-8"
          />
        </div>
        <StatusFilter
          value={statusFilter}
          counts={counts}
          onChange={onStatusChange}
        />
      </div>

      {directory.isLoading && <TableSkeleton />}

      {directory.error && (
        <ErrorBox error={directory.error as Error} onRetry={() => void directory.refetch()} />
      )}

      {directory.data && filtered.length === 0 && (
        <div className="rounded-md border bg-card p-12 text-center">
          <Users className="mx-auto h-8 w-8 text-muted-foreground" />
          <p className="mt-3 text-sm text-muted-foreground">
            {directory.data.length === 0
              ? "Keine aktiven Mitarbeitenden in diesem Unternehmen."
              : "Keine Treffer."}
          </p>
        </div>
      )}

      {filtered.length > 0 && (
        <DirectoryTable slug={slug} companyId={companyId} rows={filtered} currency={company?.currency ?? "EUR"} />
      )}
    </>
  );
}

// ── Status filter pills ─────────────────────────────────────────────────────

function StatusFilter({
  value,
  counts,
  onChange,
}: {
  value: EmployeeStatus | "all";
  counts: { total: number; fullySynced: number; notInJaveed: number; orphanWages: number };
  onChange: (status: EmployeeStatus | "all") => void;
}) {
  const items: Array<{
    key: EmployeeStatus | "all";
    label: string;
    count: number;
  }> = [
    { key: "all", label: "Alle", count: counts.total },
    { key: "fully-synced", label: "In Javeed", count: counts.fullySynced },
    { key: "not-in-javeed", label: "Nicht in Javeed", count: counts.notInJaveed },
    { key: "orphan-wages", label: "Orphan-Wages", count: counts.orphanWages },
  ];
  return (
    <div className="flex flex-wrap items-center gap-1 rounded-md border bg-card p-1">
      {items.map((it) => {
        const active = it.key === value;
        return (
          <button
            key={it.key}
            type="button"
            onClick={() => onChange(it.key)}
            className={cn(
              "rounded px-2 py-1 text-xs",
              active
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground hover:bg-accent hover:text-foreground",
            )}
          >
            {it.label}
            <span className="ml-1 tabular-nums opacity-80">{it.count}</span>
          </button>
        );
      })}
    </div>
  );
}

// ── Directory table ─────────────────────────────────────────────────────────

function DirectoryTable({
  slug,
  companyId,
  rows,
  currency,
}: {
  slug: string;
  companyId: string;
  rows: EmployeeDirectoryRowDto[];
  currency: string;
}) {
  return (
    <div className="overflow-hidden rounded-md border bg-card">
      <table className="w-full text-sm">
        <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
          <tr>
            <th className="px-3 py-2 text-left font-medium">Name</th>
            <th className="px-3 py-2 text-left font-medium">Kontakt</th>
            <th className="px-3 py-2 text-right font-medium">Stundenlohn</th>
            <th className="px-3 py-2 text-left font-medium">Beschäftigt seit</th>
            <th className="px-3 py-2 text-right font-medium">Status</th>
          </tr>
        </thead>
        <tbody className="divide-y">
          {rows.map((r) => (
            <DirectoryRow key={r.ordioIdentityId} slug={slug} companyId={companyId} row={r} currency={currency} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DirectoryRow({
  slug,
  companyId,
  row,
  currency,
}: {
  slug: string;
  companyId: string;
  row: EmployeeDirectoryRowDto;
  currency: string;
}) {
  const status = classifyEmployee(row);

  // Row only navigates when there's an Employment to look at. Plain Ordio
  // identities without Javeed state don't have a meaningful detail page yet.
  const linkable = row.employmentId !== null;
  const NameCell = linkable ? (
    <Link
      to="/o/$slug/employees/$companyId/$ordioIdentityId"
      params={{ slug, companyId, ordioIdentityId: row.ordioIdentityId }}
      className="font-medium hover:underline"
    >
      {row.employeeDisplayName}
    </Link>
  ) : (
    <span className="font-medium">{row.employeeDisplayName}</span>
  );

  return (
    <tr>
      <td className="px-3 py-2">
        <div className="flex items-center gap-2">
          {NameCell}
          {!row.isActiveInOrdio && (
            <span className="text-[10px] uppercase text-muted-foreground">inaktiv</span>
          )}
        </div>
        <div className="text-xs text-muted-foreground">{row.ordioEmployeeId}</div>
      </td>
      <td className="px-3 py-2 text-xs text-muted-foreground">
        {row.email && <div className="truncate">{row.email}</div>}
        {row.phone && <div>{row.phone}</div>}
        {!row.email && !row.phone && <span>—</span>}
      </td>
      <td className="px-3 py-2 text-right tabular-nums">
        {row.currentHourlyRate != null
          ? `${formatNumber(row.currentHourlyRate)} ${currency}`
          : "—"}
        {row.currentWageType && (
          <div className="text-[10px] uppercase text-muted-foreground">{row.currentWageType}</div>
        )}
      </td>
      <td className="px-3 py-2 text-muted-foreground">
        {row.startedOn ? formatRelative(row.startedOn) ?? row.startedOn : "—"}
      </td>
      <td className="px-3 py-2 text-right">
        <StatusBadge status={status} />
      </td>
    </tr>
  );
}

function StatusBadge({ status }: { status: EmployeeStatus }) {
  const map: Record<EmployeeStatus, { label: string; cls: string }> = {
    "fully-synced": { label: "In Javeed", cls: "bg-emerald-100 text-emerald-700" },
    "not-in-javeed": { label: "Nicht in Javeed", cls: "bg-muted text-muted-foreground" },
    "orphan-wages": { label: "Orphan-Wages", cls: "bg-amber-100 text-amber-800" },
  };
  const { label, cls } = map[status];
  return (
    <span className={cn("inline-flex items-center rounded-full px-2 py-0.5 text-xs", cls)}>
      {label}
    </span>
  );
}

// ── Skeletons / errors ──────────────────────────────────────────────────────

function TableSkeleton() {
  return (
    <div className="overflow-hidden rounded-md border bg-card">
      {Array.from({ length: 6 }).map((_, i) => (
        <Skeleton key={i} className="m-3 h-8" />
      ))}
    </div>
  );
}

function ErrorBox({ error, onRetry }: { error: Error; onRetry: () => void }) {
  const status = error instanceof ApiError ? error.status : null;
  return (
    <div className="rounded-md border border-destructive/40 bg-destructive/5 p-4">
      <div className="flex items-start gap-2 text-sm text-destructive">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
        <span>
          {status === 403
            ? "Keine Berechtigung."
            : `Konnte Mitarbeitende nicht laden: ${error.message}`}
        </span>
      </div>
      <Button variant="outline" size="sm" className="mt-3" onClick={onRetry}>
        Erneut versuchen
      </Button>
    </div>
  );
}
